import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AuthImg } from "@/lib/authMedia";
import {
  decideFaceLockEnhance, faceLockErrorText, fetchFaceLockEnhance, rollbackFaceLockEnhance,
  type EnhanceRow, type FaceLockAvatar, type FaceLockEnhanceDetail,
} from "@/lib/facelock";

// The before-and-after of an enhancement (FaceLock Ink Scan / SharpShoot).
// PEOPLE JUDGE LIKENESS (owner rule, 2026-09-30): the avatar as it is and the
// enhanced one, drawn on the same shots with the same seed, side by side, next
// to the person's own picture of that view when there is one (for a part of
// the body scanned in an EARLIER Ink Scan, a row showing it was not faded,
// beside the picture from that scan). No score is
// ever shown; a picture the alarm flagged (no face, or clearly somebody else)
// says so in words. Nothing about the avatar changes until "Keep the enhanced
// avatar"; "Keep my current avatar" changes nothing. After keeping it, "Go
// back to the previous version" switches back, free.
//
// WHICH ONE IS THE RESULT (owner, 2026-10-08: "extremely confusing, I didn't
// understand what photo was supposed to be the result"). "Your photo / Now /
// Enhanced" read as three versions with "Now" the newest. So every column says
// what it is and which side of the change it is on ("Your real photo",
// "Current avatar (before)", "Enhanced avatar (new)"), the new one is marked as
// THE result (a "New" badge, a highlighted frame) and the current one stays
// quiet. On a phone the header scrolls away after two rows, so the column names
// stick to the top and every picture also carries its own short tag. One line
// above the rows says how to read them (each row is one pose), the buttons use
// the same words as the columns, and a pose with no real photo says so instead
// of an empty box that looks like a picture that failed to load.
//
// The column names stick only over the rows they name (2026-10-08): they and the
// rows share one box, so they leave with the last row. Pinned to the whole
// dialog they sat over the two-column portraits (calling the before portraits
// "Your real photo") and over the Keep buttons. They pin to the dialog's top
// edge, not inside its padding, so no strip of pictures shows above them. Once
// kept, the line under the title says the before one is the previous avatar.

const STILL = { width: "100%", aspectRatio: "720 / 1248", objectFit: "cover" as const, display: "block" };

type Column = "real" | "current" | "new";

function Cell({ column, url, broken, alt, empty, testid }: {
  column: Column; url: string | null; broken?: boolean; alt: string; empty?: string; testid: string;
}) {
  const { t } = useTranslation();
  const result = column === "new";
  return (
    <div className={`grid content-start gap-1 rounded-lg p-1 ${result ? "bg-primary/10" : ""}`}
      data-testid={testid} data-column={column} data-result={result ? "true" : undefined}>
      {result
        ? <div><Badge className="px-2 py-0 text-[11px]">{t("faceLock.compare.tag.new")}</Badge></div>
        : <span className="text-[11px] font-medium leading-tight text-muted-foreground">{t(`faceLock.compare.tag.${column}`)}</span>}
      <div className={`overflow-hidden rounded-md bg-muted ${result ? "border-2 border-primary" : "border border-border"}`}>
        {url ? <AuthImg src={url} alt={alt} loading="lazy" style={STILL} />
          : <div style={STILL} className="flex items-center justify-center p-2 text-center text-xs text-muted-foreground">{empty || t("faceLock.compare.none")}</div>}
      </div>
      {broken && (
        <span className="flex items-start gap-1 text-xs leading-snug text-amber-500">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden /> {t("faceLock.compare.broken")}
        </span>
      )}
    </div>
  );
}

export default function EnhanceCompare({ avatar, open, onOpenChange, onChanged }: {
  avatar: FaceLockAvatar | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The avatar changed (kept, not kept, or gone back): read the list again. */
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [detail, setDetail] = React.useState<FaceLockEnhanceDetail | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState<"keep" | "drop" | "back" | null>(null);
  const [error, setError] = React.useState("");
  const [done, setDone] = React.useState<"kept" | "dropped" | "back" | null>(null);
  const avatarId = avatar?.id || "";

  React.useEffect(() => {
    if (!open || !avatarId) return;
    let alive = true;
    setLoading(true);
    setError("");
    setDone(null);
    void fetchFaceLockEnhance(avatarId).then((res) => {
      if (!alive) return;
      setLoading(false);
      if (res.ok === false) { setError(faceLockErrorText(t, res)); return; }
      setDetail(res.data);
    });
    return () => { alive = false; };
  }, [open, avatarId, t]);

  if (!avatar) return null;
  const d = detail;
  const showReal = !!d && d.rows.some((r) => r.real_url);
  // Once kept, the "before" avatar is no longer the current one: it is the version "Go back" returns to.
  const kept = d?.state === "accepted";
  const beforeLabel = t(kept ? "faceLock.compare.col.previous" : "faceLock.compare.col.current");
  const newLabel = t("faceLock.compare.col.new");
  const cols = showReal ? "grid-cols-3" : "grid-cols-2";

  // A row says plainly what it shows: "Face: Front" (three views are all called "Front" on their own),
  // a mark by its kind and place ("Tattoo: Under the left eye"), with the person's own words under it.
  const label = (r: EnhanceRow): { text: string; what: string } => {
    if (r.place) {
      const mark = d?.marks.find((m) => m.place === r.place);
      const kindKey = `faceLock.marks.kind.${mark?.kind}`;
      const kind = mark && i18n.exists(kindKey) ? t(kindKey) : t("faceLock.compare.mark");
      return { text: t("faceLock.compare.markRow", { kind, place: t(`faceLock.marks.place.${r.place}`) }), what: mark?.what || "" };
    }
    if (r.section) return { text: t("faceLock.compare.sectionRow", { section: t(`faceLock.marks.section.${r.section}`) }), what: "" };
    if (r.shot.startsWith("scene_")) {
      const key = `faceLock.compare.scene.${r.shot}`;
      return { text: i18n.exists(key) ? t(key) : r.shot, what: "" };
    }
    const viewKey = `faceLock.scan.bucket.${r.shot}`;
    const partKey = `faceLock.compare.part.${r.shot.split("_")[0]}`;
    if (!i18n.exists(viewKey)) return { text: r.shot, what: "" };
    return { text: i18n.exists(partKey) ? t("faceLock.compare.poseRow", { part: t(partKey), view: t(viewKey) }) : t(viewKey), what: "" };
  };

  const decide = async (keep: boolean) => {
    if (!d || busy) return;
    setBusy(keep ? "keep" : "drop");
    setError("");
    const res = await decideFaceLockEnhance(avatar.id, d.id, keep);
    setBusy(null);
    if (res.ok === false) { setError(faceLockErrorText(t, res)); return; }
    setDone(keep ? "kept" : "dropped");
    onChanged();
  };

  const goBack = async () => {
    if (busy) return;
    setBusy("back");
    setError("");
    const res = await rollbackFaceLockEnhance(avatar.id);
    setBusy(null);
    if (res.ok === false) { setError(faceLockErrorText(t, res)); return; }
    setDone("back");
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent overlayClassName="z-[400]" className="z-[400] max-h-[94vh] overflow-y-auto sm:max-w-5xl" data-testid="enhance-compare">
        <DialogHeader>
          <DialogTitle>{t("faceLock.compare.title", { name: avatar.name })}</DialogTitle>
          <DialogDescription data-testid="enhance-lead">{t(kept ? "faceLock.compare.leadKept" : "faceLock.compare.lead")}</DialogDescription>
        </DialogHeader>

        {loading && <p className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> {t("faceLock.compare.loading")}</p>}

        {d && !done && (
          <>
            <p className="rounded-md border border-border bg-muted/40 p-2 text-sm" data-testid="enhance-how">
              {t(kept ? "faceLock.compare.howKept" : showReal ? "faceLock.compare.how" : "faceLock.compare.howNoReal")}
            </p>
            <div className="grid gap-3" data-testid="enhance-table">
              <div className={`sticky -top-6 z-10 -mt-4 grid items-end gap-2 bg-background pb-2 pt-8 text-xs font-semibold leading-tight ${cols}`} data-testid="enhance-columns">
                {showReal && <span className="px-1" data-testid="enhance-col-real">{t("faceLock.compare.col.real")}</span>}
                <span className="px-1 text-muted-foreground" data-testid="enhance-col-current">{beforeLabel}</span>
                <span className="grid justify-items-start gap-1 rounded-md bg-primary/10 px-1 py-1 text-foreground" data-testid="enhance-col-new" data-result="true">
                  <Badge className="px-2 py-0 text-[11px]">{t("faceLock.compare.tag.new")}</Badge>
                  {newLabel}
                </span>
              </div>
              <div className="grid gap-3" data-testid="enhance-rows">
                {d.rows.map((r) => {
                  const { text, what } = label(r);
                  return (
                    <div key={r.shot} data-testid={`enhance-row-${r.shot}`} className="grid gap-1 border-t border-border pt-3 first:border-t-0 first:pt-0">
                      <span className="text-sm font-medium" data-testid={`enhance-row-label-${r.shot}`}>{text}</span>
                      {what && <span className="text-xs text-muted-foreground">{what}</span>}
                      <div className={`grid items-start gap-2 ${cols}`}>
                        {showReal && <Cell column="real" url={r.real_url} alt={t("faceLock.compare.col.real")} empty={t("faceLock.compare.noReal")} testid={`enhance-real-${r.shot}`} />}
                        <Cell column="current" url={r.base_url} broken={r.base_broken} alt={beforeLabel} testid={`enhance-base-${r.shot}`} />
                        <Cell column="new" url={r.new_url} broken={r.new_broken} alt={newLabel} testid={`enhance-new-${r.shot}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {(d.portraits.base.length > 0 || d.portraits.new.length > 0) && (
              <div className="grid gap-2" data-testid="enhance-portraits">
                <h3 className="text-sm font-semibold">{t("faceLock.compare.portraits")}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid content-start gap-1 rounded-lg p-1">
                    <span className="text-xs text-muted-foreground">{beforeLabel}</span>
                    <div className="grid grid-cols-2 gap-1">{d.portraits.base.map((u) => <AuthImg key={u} src={u} alt={beforeLabel} loading="lazy" style={STILL} />)}</div>
                  </div>
                  <div className="grid content-start gap-1 rounded-lg bg-primary/10 p-1" data-result="true">
                    <span className="flex flex-wrap items-center gap-1 text-xs font-semibold">
                      <Badge className="px-2 py-0 text-[11px]">{t("faceLock.compare.tag.new")}</Badge> {newLabel}
                    </span>
                    <div className="grid grid-cols-2 gap-1">{d.portraits.new.map((u) => <AuthImg key={u} src={u} alt={newLabel} loading="lazy" style={STILL} />)}</div>
                  </div>
                </div>
              </div>
            )}

            {d.can_decide && (
              <div className="grid gap-2 border-t border-border pt-3">
                <p className="flex items-start gap-2 text-sm"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" aria-hidden />
                  {d.price > 0 ? t("faceLock.compare.decidePaid", { count: d.price }) : t("faceLock.compare.decideFree")}</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" data-testid="enhance-keep" disabled={!!busy} onClick={() => void decide(true)}>
                    {busy === "keep" ? t("faceLock.compare.saving") : t("faceLock.compare.keepNew")}
                  </Button>
                  <Button type="button" variant="outline" data-testid="enhance-drop" disabled={!!busy} onClick={() => void decide(false)}>
                    {busy === "drop" ? t("faceLock.compare.saving") : t("faceLock.compare.keepOld")}
                  </Button>
                </div>
              </div>
            )}
            {d.can_rollback && (
              <div className="grid gap-2 border-t border-border pt-3">
                <p className="text-sm">{t("faceLock.compare.inUse")}</p>
                <Button type="button" variant="outline" className="w-fit" data-testid="enhance-rollback" disabled={!!busy} onClick={() => void goBack()}>
                  <RotateCcw aria-hidden /> {busy === "back" ? t("faceLock.compare.saving") : t("faceLock.compare.rollback")}
                </Button>
              </div>
            )}
          </>
        )}

        {done && (
          <div className="grid gap-3" data-testid="enhance-done" data-done={done}>
            <p>{t(`faceLock.compare.done.${done}`)}</p>
            <Button type="button" className="w-fit" onClick={() => onOpenChange(false)}>{t("faceLock.compare.close")}</Button>
          </div>
        )}
        {error && <div role="alert" data-testid="enhance-compare-error" className="text-sm text-red-500">{error}</div>}
      </DialogContent>
    </Dialog>
  );
}
